/**
 * Helpers for POST /api/repairing_orders (plain JSON + multipart) vs legacy
 * show_repairing_order / add_repairing_order / edit_repairing_order routes.
 */

import { isErpApiSuccess } from "../erpClient";

export { isErpApiSuccess as isRepairingOrdersApiSuccess };

/** Re-export: same payment-line sum logic as purchases/sales adapters. */
export { sumPaymentAmounts } from "./purchasesErpAdapter";

const toNumericStatus = (row) => {
  // Prefer explicit numeric flags sent by backend for legacy UI compatibility.
  const explicit = row?.status_code ?? row?.legacy_status;
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    const n = Number(explicit);
    if (!Number.isNaN(n)) return n;
  }

  const s = String(row?.status ?? "").trim().toUpperCase();
  if (s === "1" || s === "OPEN" || s === "ACTIVE") return 1;
  if (s === "0" || s === "CANCELLED" || s === "INACTIVE") return 0;
  if (s !== "") return 2;
  return "";
};

const normalizeRepairingOrderEntry = (entry = {}) => ({
  ...entry,
  // Legacy/UI aliases consumed across RepairingOrder page/modals
  product_group:
    entry?.product_group ?? entry?.product_group_id ?? entry?.product_type ?? "",
  product_group_id: entry?.product_group_id ?? entry?.product_group ?? "",
  pcs: entry?.pcs ?? entry?.pieces ?? "",
  gwt: entry?.gwt ?? entry?.current_weight ?? "",
  lwt:
    entry?.lwt ??
    entry?.lwt_customer ??
    entry?.lwt_vepari ??
    entry?.less_weight ??
    "",
  nwt:
    entry?.nwt ??
    entry?.exp_weight ??
    entry?.expected_weight ??
    entry?.net_weight ??
    "",
  exp_weight:
    entry?.exp_weight ?? entry?.expected_weight ?? entry?.nwt ?? "",
  current_weight: entry?.current_weight ?? entry?.gwt ?? "",
  issue_description:
    entry?.issue_description ?? entry?.description ?? entry?.remarks ?? "",
  // Amount aliases used by RepairingOrder modal/table
  lbr_amount:
    entry?.lbr_amount ??
    entry?.labor_amount ??
    entry?.labor_charge ??
    entry?.lbr_amt ??
    0,
  labor_amount:
    entry?.labor_amount ??
    entry?.labor_charge ??
    entry?.lbr_amount ??
    entry?.lbr_amt ??
    0,
  oth_amount:
    entry?.oth_amount ??
    entry?.other_amount ??
    entry?.oth_amt ??
    entry?.other_charge ??
    0,
  product_group_name:
    entry?.product_group_name ??
    entry?.product_type ??
    entry?.product_group ??
    "",
  item_category_name:
    entry?.item_category_name ??
    entry?.itemCategoryName ??
    "",
});

export const normalizeRepairingOrderRow = (row) => {
  const normalizedStatus = toNumericStatus(row);
  const rawEntries =
    row?.repairing_order_entry ??
    row?.entries ??
    row?.items ??
    row?.repairing_entries ??
    [];

  return {
    ...row,
    order_number: row?.order_number ?? row?.doc_number ?? row?.token_no ?? "",
    token_no: row?.token_no ?? row?.doc_number ?? row?.order_number ?? "",
    employee_name:
      row?.employee_name ?? row?.created_by_name ?? row?.employee ?? "N/A",
    customer_name:
      row?.customer_name ?? row?.user_name ?? row?.name ?? row?.customer ?? "",
    user_name: row?.user_name ?? row?.customer_name ?? row?.name ?? "",
    user_mobile: row?.user_mobile ?? row?.customer_mobile ?? row?.mobile ?? "",
    exp_delivery: row?.exp_delivery ?? row?.expected_delivery ?? "",
    final_amount:
      row?.final_amount ?? row?.grand_total ?? row?.total_amount ?? 0,
    repairing_order_entry: Array.isArray(rawEntries)
      ? rawEntries.map(normalizeRepairingOrderEntry)
      : [],
    status_code:
      row?.status_code ?? (normalizedStatus === "" ? undefined : normalizedStatus),
    legacy_status:
      row?.legacy_status ?? (normalizedStatus === "" ? undefined : normalizedStatus),
    status: normalizedStatus,
  };
};

export function normalizeRepairingOrderListFromResponse(res) {
  if (!isErpApiSuccess(res)) return [];
  const d = res.data;
  if (Array.isArray(d)) return d.map(normalizeRepairingOrderRow);
  if (Array.isArray(d?.items)) return d.items.map(normalizeRepairingOrderRow);
  if (Array.isArray(d?.repairing_order_details)) {
    return d.repairing_order_details.map(normalizeRepairingOrderRow);
  }
  if (Array.isArray(d?.repairing_orders)) {
    return d.repairing_orders.map(normalizeRepairingOrderRow);
  }
  if (Array.isArray(d?.records)) return d.records.map(normalizeRepairingOrderRow);
  if (Array.isArray(d?.data)) return d.data.map(normalizeRepairingOrderRow);
  if (d && typeof d === "object" && (d.id != null || d.doc_number)) {
    return [normalizeRepairingOrderRow(d)];
  }
  return [];
}

export function createRepairingOrderIdempotencyKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
